import { useState, useMemo } from 'react'
import { documentTypeFromNumber, documentTypeLabel } from '../lib/personDocument'
import { Box, Flex, Text, VStack, Grid, Input, Spinner, ConfirmDialog, useT } from '@minetrace/ui'
import {
  LuUsers as Users,
  LuSearch as Search,
  LuUserPlus as UserPlus,
  LuTrash2 as Trash2,
  LuX as X,
  LuShieldCheck as ShieldCheck,
  LuCircleAlert as AlertCircle,
  LuGlobe as Globe,
} from 'react-icons/lu'
import { useForm, Controller, useFormColors, FormSection } from '@minetrace/ui/forms'
import { FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { PERU_PHONE_REGEX } from '../lib/validators'
import { toaster } from '@minetrace/ui'
import { personsService } from '../api/services'
import { useRequest } from '../hooks'
import type { CompanyPersonResponse } from '../api/models/response'
import type { PersonResponse } from '../api/models/response/person'
import type { AddCompanyPersonRequest } from '../api/models/request/company'
import CharCounter from './CharCounter'
import { PersonFieldLengths } from '../lib/fieldLengths'
import { sanitizePhone } from '../lib/inputSanitizers'

// Schema for adding a new person (person not found in DB)
const newPersonSchema = z.object({
  names: z.string().min(1, 'shared.contactos.zodNamesRequired').max(100),
  paternalSurname: z.string().min(1, 'shared.contactos.zodPaternalRequired').max(100),
  maternalSurname: z.string().max(100).optional(),
  email: z.string().email('shared.contactos.zodEmailInvalid').min(1, 'shared.contactos.zodEmailRequired').max(100),
  // Misma regla que el resto del sistema (peruPhone), pero con la clave i18n como
  // mensaje: este componente pasa cada error de zod por t(). Ver lib/validators.
  phoneMobile: z.string()
    .min(1, 'shared.contactos.zodPhoneRequired')
    .regex(PERU_PHONE_REGEX, 'shared.contactos.zodPhoneFormat'),
  contactType: z.number().min(1, 'shared.contactos.zodContactTypeRequired'),
})

// Schema for adding a found person (only need contact type, person data already exists)
const foundPersonSchema = z.object({
  contactType: z.number().min(1, 'shared.contactos.zodContactTypeRequired'),
})

type NewPersonFormData = z.infer<typeof newPersonSchema>
type FoundPersonFormData = z.infer<typeof foundPersonSchema>

interface ContactosSectionProps {
  companyId: string | null
  persons: CompanyPersonResponse[]
  onPersonsChange: (persons: CompanyPersonResponse[]) => void
  addPerson: (companyId: string, data: AddCompanyPersonRequest) => Promise<CompanyPersonResponse>
  removePerson: (companyId: string, personId: string) => Promise<void>
  onPendingContactAdd?: (localId: string, request: AddCompanyPersonRequest) => void
  onPendingContactRemove?: (localId: string) => void
  description?: string
  descriptionTid?: string
  /** Solo lectura (evaluación, detalle): oculta la búsqueda/alta y el botón eliminar. */
  readonly?: boolean
}

export default function ContactosSection({ companyId, persons, onPersonsChange, addPerson, removePerson, onPendingContactAdd, onPendingContactRemove, description, descriptionTid, readonly = false }: ContactosSectionProps) {
  const c = useFormColors()
  const { t } = useT()

  // i18n'd contact types (rebuilt per render since t is in closure)
  const CONTACT_TYPES = useMemo(() => [
    { value: 0, label: t('shared.contactos.typeSelectPlaceholder') },
    { value: 1, label: t('shared.contactos.types.legalRep') },
    { value: 2, label: t('shared.contactos.types.admin') },
    { value: 3, label: t('shared.contactos.types.commercial') },
    { value: 4, label: t('shared.contactos.types.finance') },
    { value: 5, label: t('shared.contactos.types.technical') },
  ], [t])

  // UI state (not form fields)
  const [searchDoc, setSearchDoc] = useState('')
  const [searchingPerson, setSearchingPerson] = useState(false)
  const [foundPerson, setFoundPerson] = useState<PersonResponse | null>(null)
  const [personNotFound, setPersonNotFound] = useState(false)
  const [sunatSource, setSunatSource] = useState(false)
  const [searchedDoc, setSearchedDoc] = useState('')
  const [removeTarget, setRemoveTarget] = useState<CompanyPersonResponse | null>(null)

  // Form for new person (person not found in DB)
  const newPersonForm = useForm<NewPersonFormData>({
    resolver: zodResolver(newPersonSchema),
    defaultValues: {
      names: '',
      paternalSurname: '',
      maternalSurname: '',
      email: '',
      phoneMobile: '',
      contactType: 0,
    },
  })

  // Form for found person (existing person, may need extra fields)
  const foundPersonForm = useForm<FoundPersonFormData>({
    resolver: zodResolver(foundPersonSchema),
    defaultValues: {
      contactType: 0,
    },
  })

  const inputStyles = {
    bg: c.bgInput,
    border: '1px solid',
    borderColor: 'gray.200',
    borderRadius: 'lg',
    color: c.text,
    h: '42px',
    fontSize: 'sm',
    _placeholder: { color: c.textMuted },
    _focus: { outline: 'none', borderColor: '#b4532a', ring: '1px', ringColor: '#b4532a' },
  } as const

  const selectStyles: React.CSSProperties = {
    width: '100%',
    height: '42px',
    padding: '0 0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid #e8dfd1',
    fontSize: '0.875rem',
    background: c.bgInput,
    color: c.text,
    outline: 'none',
  }

  const { execute: executeAddPerson, isFetching: addingPerson } = useRequest(
    (params: { companyId: string; data: AddCompanyPersonRequest }) => addPerson(params.companyId, params.data)
  )

  const handleSearchPerson = async () => {
    const doc = searchDoc.trim()
    if (!doc) return
    setSearchedDoc(doc)
    setSearchingPerson(true)
    setFoundPerson(null)
    setPersonNotFound(false)
    setSunatSource(false)
    foundPersonForm.reset({ contactType: 0 })
    newPersonForm.reset({ names: '', paternalSurname: '', maternalSurname: '', email: '', phoneMobile: '', contactType: 0 })
    try {
      if (doc.length === 8 && /^\d+$/.test(doc)) {
        const lookup = await personsService.lookupDni(doc)
        if (lookup.foundInDatabase && lookup.personId) {
          const fullPerson = await personsService.getById(lookup.personId)
          setFoundPerson(fullPerson)
        } else if (lookup.source === 'sunat') {
          setPersonNotFound(true)
          setSunatSource(true)
          newPersonForm.reset({
            names: lookup.names ?? '',
            paternalSurname: lookup.paternalSurname ?? '',
            maternalSurname: lookup.maternalSurname ?? '',
            email: '',
            phoneMobile: '',
            contactType: 0,
          })
        } else {
          setPersonNotFound(true)
        }
      } else {
        const data = await personsService.searchByDoc(doc)
        if (data) {
          setFoundPerson(data)
        } else {
          setPersonNotFound(true)
        }
      }
    } catch {
      setPersonNotFound(true)
    } finally {
      setSearchingPerson(false)
      setSearchDoc('')
    }
  }

  const handleCancelSearch = () => {
    setFoundPerson(null)
    setPersonNotFound(false)
    setSunatSource(false)
    setSearchedDoc('')
    foundPersonForm.reset({ contactType: 0 })
    newPersonForm.reset({ names: '', paternalSurname: '', maternalSurname: '', email: '', phoneMobile: '', contactType: 0 })
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSearchPerson()
    }
  }

  const getContactTypeName = (type: number) => {
    return CONTACT_TYPES.find(ct => ct.value === type)?.label ?? t('shared.contactos.unknownType')
  }

  const handleAddFoundPerson = async (data: FoundPersonFormData) => {
    if (!foundPerson) return

    if (!companyId) {
      // Memory mode: add locally without API call
      const localId = `local-${Date.now()}`
      const localPerson: CompanyPersonResponse = {
        id: localId,
        personId: foundPerson.id,
        fullName: foundPerson.fullName,
        identityDoc: foundPerson.identityDoc,
        contactType: data.contactType,
        contactTypeName: getContactTypeName(data.contactType),
        email: foundPerson.email,
        phone: foundPerson.phoneMobile,
      }
      onPersonsChange([...persons, localPerson])
      onPendingContactAdd?.(localId, { personId: foundPerson.id, contactType: data.contactType })
      setSearchDoc(''); setSearchedDoc(''); setFoundPerson(null); setPersonNotFound(false); setSunatSource(false)
      foundPersonForm.reset({ contactType: 0 })
      toaster.success({ description: t('shared.contactos.addedToast') })
      return
    }

    const requestData: AddCompanyPersonRequest = {
      personId: foundPerson.id,
      contactType: data.contactType,
    }
    const result = await executeAddPerson({ companyId, data: requestData })
    if (!result) return
    onPersonsChange([...persons, result])
    setSearchDoc(''); setSearchedDoc(''); setFoundPerson(null); setPersonNotFound(false); setSunatSource(false)
    foundPersonForm.reset({ contactType: 0 })
    toaster.success({ description: t('shared.contactos.addedSuccessToast') })
  }

  const handleAddNewPerson = async (data: NewPersonFormData) => {
    if (!companyId) {
      // Memory mode: add locally without API call
      const localId = `local-${Date.now()}`
      const fullName = [data.names, data.paternalSurname, data.maternalSurname].filter(Boolean).join(' ')
      const localPerson: CompanyPersonResponse = {
        id: localId,
        personId: '',
        fullName,
        identityDoc: searchedDoc,
        contactType: data.contactType,
        contactTypeName: getContactTypeName(data.contactType),
        email: data.email || null,
        phone: data.phoneMobile || null,
      }
      onPersonsChange([...persons, localPerson])
      onPendingContactAdd?.(localId, {
        contactType: data.contactType,
        documentType: documentTypeFromNumber(searchedDoc),
        identityDoc: searchedDoc,
        names: data.names,
        paternalSurname: data.paternalSurname,
        maternalSurname: data.maternalSurname || undefined,
        email: data.email || undefined,
        phoneMobile: data.phoneMobile || undefined,
      })
      setSearchDoc(''); setSearchedDoc(''); setFoundPerson(null); setPersonNotFound(false); setSunatSource(false)
      newPersonForm.reset({ names: '', paternalSurname: '', maternalSurname: '', email: '', phoneMobile: '', contactType: 0 })
      toaster.success({ description: t('shared.contactos.addedToast') })
      return
    }

    const requestData: AddCompanyPersonRequest = {
      contactType: data.contactType,
      documentType: documentTypeFromNumber(searchedDoc),
      identityDoc: searchedDoc,
      names: data.names,
      paternalSurname: data.paternalSurname,
      maternalSurname: data.maternalSurname || undefined,
      email: data.email || undefined,
      phoneMobile: data.phoneMobile || undefined,
    }
    const result = await executeAddPerson({ companyId, data: requestData })
    if (!result) return
    onPersonsChange([...persons, result])
    setSearchDoc(''); setSearchedDoc(''); setFoundPerson(null); setPersonNotFound(false); setSunatSource(false)
    newPersonForm.reset({ names: '', paternalSurname: '', maternalSurname: '', email: '', phoneMobile: '', contactType: 0 })
    toaster.success({ description: t('shared.contactos.addedSuccessToast') })
  }

  const handleConfirmRemove = async () => {
    if (!removeTarget) return

    if (!companyId || removeTarget.id.startsWith('local-')) {
      // Memory mode: remove locally
      onPersonsChange(persons.filter((x) => x.id !== removeTarget.id))
      onPendingContactRemove?.(removeTarget.id)
      toaster.success({ description: t('shared.contactos.removedToast') })
      setRemoveTarget(null)
      return
    }

    try {
      await removePerson(companyId, removeTarget.id)
      onPersonsChange(persons.filter((x) => x.id !== removeTarget.id))
      toaster.success({ description: t('shared.contactos.removedToast') })
    } catch {
      // silently fail
    } finally {
      setRemoveTarget(null)
    }
  }

  const getInitials = (name: string) => {
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
    return (parts[0]?.[0] ?? '?').toUpperCase()
  }

  const resolvedDescription = descriptionTid ? t(descriptionTid) : (description ?? t('shared.contactos.defaultDescription'))

  return (
    <VStack gap={6} alignItems="stretch">
      <FormSection icon={Users} title={t('shared.contactos.title')} description={resolvedDescription}>
        <Grid templateColumns={readonly ? '1fr' : { base: '1fr', lg: '1fr 400px' }} gap={6}>
          {/* -- Left: Search & Add -- */}
          {!readonly && (
          <VStack gap={5} alignItems="stretch">

            {/* Search bar */}
            <Box>
              <Text tid="shared.contactos.searchTitle" fontSize="sm" fontWeight="600" color={c.text} mb={3} />
              <Text tid="shared.contactos.searchHelper" fontSize="xs" color={c.textMuted} mb={2} />
              <Flex gap={2}>
                <Input
                  value={searchDoc}
                  // DNI (8 dígitos) o CE (9-12 alfanuméricos): filtrar en vivo evita que
                  // se busque con espacios o guiones pegados y el lookup falle sin motivo
                  // visible para el usuario.
                  onChange={(e) => setSearchDoc(e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 12))}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={t('shared.contactos.searchPlaceholder')}
                  maxLength={12}
                  fontFamily="monospace"
                  {...inputStyles}
                />

                <Box
                  as="button"
                  type="button"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={2}
                  px={4}
                  h="42px"
                  bg="#b4532a"
                  color="white"
                  borderRadius="lg"
                  fontSize="sm"
                  fontWeight="600"
                  cursor={!searchDoc.trim() || searchingPerson ? 'not-allowed' : 'pointer'}
                  opacity={!searchDoc.trim() || searchingPerson ? 0.5 : 1}
                  flexShrink={0}
                  whiteSpace="nowrap"
                  _hover={{ bg: '#085a30' }}
                  onClick={handleSearchPerson}
                  disabled={!searchDoc.trim() || searchingPerson}
                >
                  {searchingPerson ? <Spinner size="sm" color="white" /> : <Search size={16} />}
                  {t('shared.contactos.searchButton')}
                </Box>
              </Flex>
            </Box>

            {/* -- Found person card -- */}
            {foundPerson && (
              <Box
                bg="white"
                border="1px solid"
                borderColor="#dcefed"
                borderRadius="xl"
                p={5}
                shadow="0 1px 3px rgba(0,0,0,0.06)"
              >
                {/* Header */}
                <Flex justifyContent="space-between" alignItems="center" mb={4}>
                  <Text tid="shared.contactos.dbMatch" fontSize="xs" fontWeight="700" color={c.textMuted} letterSpacing="0.05em" textTransform="uppercase" />
                  <Flex gap={2} alignItems="center">
                    <Flex alignItems="center" gap={1} bg="#dcefed" color="#1d6664" px={2} py={1} borderRadius="full" fontSize="10px" fontWeight="700">
                      <ShieldCheck size={12} />
                      {t('shared.contactos.verified')}
                    </Flex>
                    <Box as="button" type="button" p={1} borderRadius="full" color={c.textMuted} cursor="pointer"
                      _hover={{ bg: 'gray.100', color: c.text }} onClick={handleCancelSearch}>
                      <X size={16} />
                    </Box>
                  </Flex>
                </Flex>

                {/* Person info */}
                <Flex gap={4} mb={4}>
                  <Flex
                    w="56px" h="56px" borderRadius="full"
                    bg="#dcefed" color="#1d6664"
                    alignItems="center" justifyContent="center"
                    fontSize="lg" fontWeight="700" flexShrink={0}
                  >
                    {getInitials(foundPerson.fullName)}
                  </Flex>
                  <Box>
                    <Text fontSize="md" fontWeight="700" color={c.text}>{foundPerson.fullName}</Text>
                    {foundPerson.email && (
                      <Text fontSize="xs" color={c.textMuted}>{foundPerson.email}</Text>
                    )}
                    <Flex gap={4} mt={2}>
                      <Box>
                        <Text tid="shared.contactos.dni" fontSize="10px" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.05em" />
                        <Text fontSize="sm" fontWeight="500" color={c.text}>{foundPerson.identityDoc}</Text>
                      </Box>
                      {foundPerson.phoneMobile && (
                        <Box>
                          <Text tid="shared.contactos.phone" fontSize="10px" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.05em" />
                          <Text fontSize="sm" fontWeight="500" color={c.text}>{foundPerson.phoneMobile}</Text>
                        </Box>
                      )}
                    </Flex>
                  </Box>
                </Flex>

                {/* Email/Phone inputs if missing */}
                {/* Contact type + Confirm button */}
                <Flex gap={3} alignItems="flex-end">
                  <Box flex={1}>
                    <Text tid="shared.contactos.contactTypeLabel" fontSize="xs" fontWeight="600" color={c.textMuted} mb={1} />
                    <Controller
                      name="contactType"
                      control={foundPersonForm.control}
                      render={({ field }) => (
                        <select value={field.value} onChange={(e) => field.onChange(parseInt(e.target.value))} style={selectStyles}>
                          {CONTACT_TYPES.map((ct) => (<option key={ct.value} value={ct.value} disabled={ct.value === 0}>{ct.label}</option>))}
                        </select>
                      )}
                    />
                    {foundPersonForm.formState.errors.contactType && (
                      <Text fontSize="xs" color={c.error} mt={1}>{t(foundPersonForm.formState.errors.contactType.message ?? '')}</Text>
                    )}
                  </Box>
                  <Box
                    as="button"
                    type="button"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    px={5}
                    h="42px"
                    bg="#b4532a"
                    color="white"
                    borderRadius="lg"
                    fontSize="sm"
                    fontWeight="600"
                    cursor={addingPerson ? 'not-allowed' : 'pointer'}
                    opacity={addingPerson ? 0.5 : 1}
                    whiteSpace="nowrap"
                    _hover={{ bg: '#085a30' }}
                    onClick={foundPersonForm.handleSubmit(handleAddFoundPerson)}
                    disabled={addingPerson}
                  >
                    {addingPerson ? <Spinner size="sm" color="white" /> : <UserPlus size={16} />}
                    {t('shared.contactos.confirmAdd')}
                  </Box>
                </Flex>
              </Box>
            )}

            {/* -- New person form -- */}
            {personNotFound && (
              <FormProvider {...newPersonForm}>
              <Box
                bg="white"
                border="1px solid"
                borderColor="#e8dfd1"
                borderRadius="xl"
                p={5}
                shadow="0 1px 3px rgba(0,0,0,0.06)"
              >
                {/* Header */}
                <Flex justifyContent="space-between" alignItems="center" mb={4}>
                  <Box>
                    <Text tid="shared.contactos.confirmNewContact" fontSize="sm" fontWeight="700" color={c.text} />
                    {sunatSource && (
                      <Text tid="shared.contactos.externalSource" fontSize="xs" color={c.textMuted} />
                    )}
                  </Box>
                  <Flex gap={2} alignItems="center">
                    {sunatSource ? (
                      <Flex alignItems="center" gap={1} bg="#dcefed" color="#1d6664" px={2} py={1} borderRadius="full" fontSize="10px" fontWeight="700">
                        <Globe size={12} />
                        {t('shared.contactos.sunatSource')}
                      </Flex>
                    ) : (
                      <Flex alignItems="center" gap={1} bg="#fce4e4" color="#b23a26" px={2} py={1} borderRadius="full" fontSize="10px" fontWeight="700">
                        <AlertCircle size={12} />
                        {t('shared.contactos.noMatch')}
                      </Flex>
                    )}
                    <Box as="button" type="button" p={1} borderRadius="full" color={c.textMuted} cursor="pointer"
                      _hover={{ bg: 'gray.100', color: c.text }} onClick={handleCancelSearch}>
                      <X size={16} />
                    </Box>
                  </Flex>
                </Flex>

                {/* Form fields */}
                <VStack gap={3} alignItems="stretch">
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                    <Box>
                      <Text tid="shared.contactos.namesLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
                      <Input {...newPersonForm.register('names')} placeholder={t('shared.contactos.namesPlaceholder')} maxLength={PersonFieldLengths.names} {...inputStyles} />
                      <Flex justifyContent="space-between" alignItems="center" mt={1}>
                        {newPersonForm.formState.errors.names ? (
                          <Text fontSize="xs" color={c.error}>{t(newPersonForm.formState.errors.names.message ?? '')}</Text>
                        ) : <span />}
                        <Box ml="auto"><CharCounter name="names" max={PersonFieldLengths.names} mt={0} /></Box>
                      </Flex>
                    </Box>
                    <Box>
                      <Text tid="shared.contactos.paternalLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
                      <Input {...newPersonForm.register('paternalSurname')} placeholder={t('shared.contactos.paternalPlaceholder')} maxLength={PersonFieldLengths.paternalSurname} {...inputStyles} />
                      <Flex justifyContent="space-between" alignItems="center" mt={1}>
                        {newPersonForm.formState.errors.paternalSurname ? (
                          <Text fontSize="xs" color={c.error}>{t(newPersonForm.formState.errors.paternalSurname.message ?? '')}</Text>
                        ) : <span />}
                        <Box ml="auto"><CharCounter name="paternalSurname" max={PersonFieldLengths.paternalSurname} mt={0} /></Box>
                      </Flex>
                    </Box>
                  </Grid>
                  <Box>
                    <Text tid="shared.contactos.maternalLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
                    <Input {...newPersonForm.register('maternalSurname')} placeholder={t('shared.contactos.maternalPlaceholder')} maxLength={PersonFieldLengths.maternalSurname} {...inputStyles} />
                    <CharCounter name="maternalSurname" max={PersonFieldLengths.maternalSurname} />
                  </Box>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                    <Box>
                      <Text tid="shared.contactos.dni" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
                      <Input value={searchedDoc} disabled fontFamily="monospace" maxLength={PersonFieldLengths.identityDoc} {...inputStyles} opacity={0.7} />
                    </Box>
                    <Box>
                      <Text tid="shared.contactos.emailLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
                      <Input type="email" {...newPersonForm.register('email')} placeholder={t('shared.contactos.emailPlaceholder')} maxLength={PersonFieldLengths.email} {...inputStyles} />
                      <Flex justifyContent="space-between" alignItems="center" mt={1}>
                        {newPersonForm.formState.errors.email ? (
                          <Text fontSize="xs" color={c.error}>{t(newPersonForm.formState.errors.email.message ?? '')}</Text>
                        ) : <span />}
                        <Box ml="auto"><CharCounter name="email" max={PersonFieldLengths.email} mt={0} /></Box>
                      </Flex>
                    </Box>
                  </Grid>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
                    <Box>
                      <Text tid="shared.contactos.phoneLabel" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
                      <Controller
                        name="phoneMobile"
                        control={newPersonForm.control}
                        render={({ field }) => (
                          <Input
                            value={field.value}
                            onChange={(e) => field.onChange(sanitizePhone(e.target.value).slice(0, PersonFieldLengths.phoneMobile))}
                            placeholder={t('shared.contactos.phonePlaceholder')}
                            maxLength={PersonFieldLengths.phoneMobile}
                            inputMode="tel"
                            {...inputStyles}
                          />
                        )}
                      />
                      <Flex justifyContent="space-between" alignItems="center" mt={1}>
                        {newPersonForm.formState.errors.phoneMobile ? (
                          <Text fontSize="xs" color={c.error}>{t(newPersonForm.formState.errors.phoneMobile.message ?? '')}</Text>
                        ) : <span />}
                        <Box ml="auto"><CharCounter name="phoneMobile" max={PersonFieldLengths.phoneMobile} mt={0} /></Box>
                      </Flex>
                    </Box>
                    <Box>
                      <Text tid="shared.contactos.contactTypeRequired" fontSize="xs" fontWeight="600" color={c.textMuted} textTransform="uppercase" letterSpacing="0.03em" mb={1} />
                      <Controller
                        name="contactType"
                        control={newPersonForm.control}
                        render={({ field }) => (
                          <select value={field.value} onChange={(e) => field.onChange(parseInt(e.target.value))} style={selectStyles}>
                            {CONTACT_TYPES.map((ct) => (<option key={ct.value} value={ct.value} disabled={ct.value === 0}>{ct.label}</option>))}
                          </select>
                        )}
                      />
                      {newPersonForm.formState.errors.contactType && (
                        <Text fontSize="xs" color={c.error} mt={1}>{t(newPersonForm.formState.errors.contactType.message ?? '')}</Text>
                      )}
                    </Box>
                  </Grid>
                </VStack>

                {/* Confirm button */}
                <Flex justifyContent="flex-end" mt={4}>
                  <Box
                    as="button"
                    type="button"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                    px={5}
                    h="42px"
                    bg="#b4532a"
                    color="white"
                    borderRadius="lg"
                    fontSize="sm"
                    fontWeight="600"
                    cursor={addingPerson ? 'not-allowed' : 'pointer'}
                    opacity={addingPerson ? 0.5 : 1}
                    whiteSpace="nowrap"
                    _hover={{ bg: '#085a30' }}
                    onClick={newPersonForm.handleSubmit(handleAddNewPerson)}
                    disabled={addingPerson}
                  >
                    {addingPerson ? <Spinner size="sm" color="white" /> : <UserPlus size={16} />}
                    {t('shared.contactos.confirmAdd')}
                  </Box>
                </Flex>
              </Box>
              </FormProvider>
            )}
          </VStack>
          )}

          {/* -- Right: Associated Contacts -- */}
          <Box>
            <Text tid="shared.contactos.associatedTitle" fontSize="xs" fontWeight="700" color={c.textMuted} letterSpacing="0.05em" textTransform="uppercase" mb={3} />
            {persons.length === 0 ? (
              <VStack gap={3}>
                {/* Empty slots */}
                {[1, 2, 3].map((i) => (
                  <Flex
                    key={i}
                    p={3}
                    borderRadius="lg"
                    border="1px dashed"
                    borderColor="gray.200"
                    alignItems="center"
                    justifyContent="center"
                    h="60px"
                    w="100%"
                  >
                    <Text tid="shared.contactos.emptySlot" fontSize="xs" color={c.textMuted} />
                  </Flex>
                ))}
              </VStack>
            ) : (
              <VStack gap={2} alignItems="stretch">
                {persons.map((p) => (
                  <Flex
                    key={p.id}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.100"
                    borderRadius="lg"
                    p={3}
                    alignItems="center"
                    gap={3}
                    shadow="0 1px 2px rgba(0,0,0,0.04)"
                  >
                    {/* Avatar */}
                    <Flex
                      w="40px" h="40px" borderRadius="full"
                      bg="#dcefed" color="#1d6664"
                      alignItems="center" justifyContent="center"
                      fontSize="sm" fontWeight="700" flexShrink={0}
                    >
                      {getInitials(p.fullName)}
                    </Flex>
                    {/* Info */}
                    <Box flex={1} minW={0}>
                      <Text fontSize="sm" fontWeight="600" color={c.text} truncate>{p.fullName}</Text>
                      <Flex gap={2} alignItems="center" flexWrap="wrap">
                        <Text fontSize="xs" color={c.textMuted} truncate>{p.contactTypeName}</Text>
                        {/* El documento identifica a la persona; sin el, dos contactos
                            homonimos son indistinguibles en la lista. */}
                        {p.identityDoc && (
                          <Text fontSize="xs" color={c.textMuted} fontFamily="monospace">· {documentTypeLabel(p.identityDoc)} {p.identityDoc}</Text>
                        )}
                      </Flex>
                      <Flex gap={2} flexWrap="wrap">
                        {p.email && <Text fontSize="xs" color={c.textMuted} truncate>{p.email}</Text>}
                        {p.phone && <Text fontSize="xs" color={c.textMuted}>| {p.phone}</Text>}
                      </Flex>
                    </Box>
                    {/* Remove */}
                    {!readonly && <Box
                      as="button"
                      type="button"
                      fontSize="xs"
                      color="#b23a26"
                      cursor="pointer"
                      fontWeight="600"
                      flexShrink={0}
                      _hover={{ textDecoration: 'underline' }}
                      onClick={() => setRemoveTarget(p)}
                    >
                      <Trash2 size={14} />
                    </Box>}
                  </Flex>
                ))}
              </VStack>
            )}
          </Box>
        </Grid>
      </FormSection>

      {/* Confirmation dialog for removing a contact */}
      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleConfirmRemove}
        title={t('shared.contactos.removeTitle')}
        confirmLabel={t('shared.contactos.removeConfirm')}
        variant="danger"
      >
        {t('shared.contactos.removeQuestion')} <Text as="span" fontWeight="600">{removeTarget?.fullName}</Text> {t('shared.contactos.removeFromContacts')}
      </ConfirmDialog>
    </VStack>
  )
}
